/**
 * demoPharmacyMode.ts — Core / Trak Tier Toggle for Pharmacy Demo
 *
 * Manages the "which tier is the pharmacist viewing" state.
 *
 * Architecture:
 *   - Module-level shared store with subscriber notification (same-tab reactivity)
 *   - sessionStorage persistence (survives SPA navigation)
 *   - storage event listener (cross-tab sync)
 *   - useSyncExternalStore for deterministic React subscription
 *
 * All mounted instances of usePharmacyDemoTier() re-render immediately
 * when any instance calls setTier() or toggleTier().
 *
 * Place at: src/demo/demoPharmacyMode.ts
 */

import { useSyncExternalStore, useCallback } from 'react';
import { isPharmacyDemo } from './demoPartner';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PharmacyDemoTier = 'core' | 'trak';

export interface UsePharmacyDemoTierResult {
  tier: PharmacyDemoTier;
  isCore: boolean;
  isTrak: boolean;
  toggleTier: () => void;
  setTier: (tier: PharmacyDemoTier) => void;
  isPharmacyDemo: boolean;
}

// ── Storage key ───────────────────────────────────────────────────────────────

const TIER_KEY = 'coretrak.demo.pharmacyTier';

// ── Module-level shared store ─────────────────────────────────────────────────

// Current value — single source of truth for same-tab instances
let _currentTier: PharmacyDemoTier = readFromStorage();

// Subscriber set — all mounted hook instances
const _subscribers = new Set<() => void>();

function readFromStorage(): PharmacyDemoTier {
  try {
    const raw = sessionStorage.getItem(TIER_KEY);
    if (raw === 'trak') return 'trak';
  } catch { /* ignore */ }
  return 'core';
}

function writeToStorage(tier: PharmacyDemoTier): void {
  try {
    sessionStorage.setItem(TIER_KEY, tier);
  } catch { /* ignore */ }
}

function notifySubscribers(): void {
  _subscribers.forEach(fn => fn());
}

function setTierInStore(tier: PharmacyDemoTier): void {
  _currentTier = tier;
  writeToStorage(tier);
  notifySubscribers();
}

// Cross-tab sync via storage event
// (storage event does not fire in the same tab, so this only helps other tabs/windows)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === TIER_KEY) {
      const next: PharmacyDemoTier = e.newValue === 'trak' ? 'trak' : 'core';
      if (next !== _currentTier) {
        _currentTier = next;
        notifySubscribers();
      }
    }
  });
}

// ── Store API (useSyncExternalStore shape) ────────────────────────────────────

function subscribe(listener: () => void): () => void {
  _subscribers.add(listener);
  return () => _subscribers.delete(listener);
}

function getSnapshot(): PharmacyDemoTier {
  return _currentTier;
}

function getServerSnapshot(): PharmacyDemoTier {
  return 'core';
}

// ── Public non-hook API ───────────────────────────────────────────────────────

/**
 * Synchronous read — safe outside React (e.g. route guards, non-hook logic).
 * Returns 'trak' outside pharmacy demo context so non-demo code is unaffected.
 */
export function getPharmacyDemoTier(): PharmacyDemoTier {
  if (!isPharmacyDemo()) return 'trak';
  return _currentTier;
}

/**
 * Returns true if a given feature should be locked (Core mode active).
 */
export function isPharmacyFeatureLocked(): boolean {
  return isPharmacyDemo() && getPharmacyDemoTier() === 'core';
}

// ── React hook ────────────────────────────────────────────────────────────────

/**
 * Hook for React components that need to read or toggle the pharmacy demo tier.
 * All mounted instances re-render immediately when any instance calls setTier().
 *
 * Usage:
 *   const { tier, isCore, toggleTier } = usePharmacyDemoTier();
 */
export function usePharmacyDemoTier(): UsePharmacyDemoTierResult {
  const tier = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setTier = useCallback((next: PharmacyDemoTier) => {
    setTierInStore(next);
  }, []);

  const toggleTier = useCallback(() => {
    setTierInStore(_currentTier === 'core' ? 'trak' : 'core');
  }, []);

  return {
    tier,
    isCore: tier === 'core',
    isTrak: tier === 'trak',
    toggleTier,
    setTier,
    isPharmacyDemo: isPharmacyDemo(),
  };
}
